import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { menuData, ingredientIcons } from '../../data/barMenuData';
import './CocktailDetailPage.css';

const CocktailDetailPage = () => {
  const { cocktailName } = useParams();
  const navigate = useNavigate();
  
  // Find the cocktail in all categories
  const findCocktail = () => {
    const allCocktails = Object.values(menuData.drinks.cocktails).flat();
    const otherDrinks = [
      ...menuData.drinks.beer,
      ...menuData.drinks.wine,
      ...menuData.drinks.brandy,
      ...menuData.drinks.rum,
      ...menuData.drinks.tequila,
      ...menuData.drinks.spirits.gin,
      ...menuData.drinks.spirits.vodka,
      ...menuData.drinks.softDrink,
      ...menuData.drinks.nonAlcoholic
    ];
    
    const allDrinksList = [...allCocktails, ...otherDrinks];
    return allDrinksList.find(drink => drink.name === decodeURIComponent(cocktailName));
  };
  
  const cocktail = findCocktail();
  
  if (!cocktail) {
    return (
      <div className="cocktail-detail-container">
        <h2>Cocktail not found</h2>
        <button className="back-button" onClick={() => navigate('/facilities/bar-lounge')}>Back to Menu</button>
      </div>
    );
  }
  
  // Calculate ingredient proportions
  const calculateProportions = (ingredients) => {
    const total = ingredients.length;
    return ingredients.map(() => (100 / total).toFixed(0));
  };
  
  const proportions = cocktail.ingredients ? calculateProportions(cocktail.ingredients) : [];
  
  // Determine glass type based on glass icon
  const getGlassType = (glassIcon) => {
    switch(glassIcon) {
      case '🍸': return 'martini';
      case '🍹': return 'hurricane';
      case '🥃': return 'rocks';
      case '🍷': return 'wine';
      case '🥂': return 'flute';
      case '🍺': return 'beer';
      case '🥤': return 'highball';
      default: return 'hurricane';
    }
  };
  
  const glassType = getGlassType(cocktail.glass);
  
  return (
    <div className="cocktail-detail-container">
      <button className="back-button" onClick={() => navigate('/facilities/bar-lounge')}>
        Back to Menu
      </button>
      
      <div className="cocktail-detail-content">
        <h1 className="cocktail-name">{cocktail.name}</h1>
        <p className="cocktail-price">¥{cocktail.price}</p>
        
        <div className="cocktail-visualization">
          <div className="glass-container">
            <svg className={`glass-svg ${glassType}`} viewBox="0 0 200 300" width="200" height="300">
              {glassType === 'martini' && (
                <>
                  {/* Martini glass shape - V-shaped cocktail glass */}
                  <path d="M100 220 L40 80 L160 80 Z" 
                        fill="none" 
                        stroke="#333" 
                        strokeWidth="3"/>
                  {/* Stem */}
                  <line x1="100" y1="220" x2="100" y2="260" 
                        stroke="#333" 
                        strokeWidth="3"/>
                  {/* Base */}
                  <ellipse cx="100" cy="260" rx="35" ry="5" fill="#333" opacity="0.3"/>
                  <line x1="70" y1="260" x2="130" y2="260" 
                        stroke="#333" 
                        strokeWidth="3"/>
                  
                  <clipPath id="martini-clip">
                    <path d="M100 217 L42 82 L158 82 Z"/>
                  </clipPath>
                  <g clipPath="url(#martini-clip)">
                    {cocktail.ingredients && cocktail.ingredients.map((ingredient, index) => {
                      const totalHeight = 135; // Height from bottom of glass to top
                      const y = 217 - (proportions.slice(0, index + 1).reduce((a, b) => a + parseFloat(b), 0) * totalHeight / 100);
                      const prevY = index === 0 ? 217 : 217 - (proportions.slice(0, index).reduce((a, b) => a + parseFloat(b), 0) * totalHeight / 100);
                      const color = getIngredientColor(ingredient);
                      
                      // Calculate trapezoid points for martini glass shape
                      const getWidth = (yPos) => {
                        const ratio = (217 - yPos) / totalHeight;
                        return 58 + (ratio * 58); // Width varies from 58 at bottom to 116 at top
                      };
                      
                      const topWidth = getWidth(y);
                      const bottomWidth = getWidth(prevY);
                      const leftX1 = 100 - topWidth / 2;
                      const rightX1 = 100 + topWidth / 2;
                      const leftX2 = 100 - bottomWidth / 2;
                      const rightX2 = 100 + bottomWidth / 2;
                      
                      return (
                        <path key={index}
                              className="glass-layer"
                              d={`M ${leftX1} ${y} L ${rightX1} ${y} L ${rightX2} ${prevY} L ${leftX2} ${prevY} Z`}
                              fill={color} 
                              opacity="0.8"/>
                      );
                    })}
                  </g>
                </>
              )}
              
              {glassType === 'hurricane' && (
                <>
                  <path d="M70 50 Q70 100 80 150 Q90 200 90 250 L110 250 Q110 200 120 150 Q130 100 130 50 Z" 
                        fill="none" 
                        stroke="#333" 
                        strokeWidth="2"/>
                  <ellipse cx="100" cy="250" rx="20" ry="3" fill="#333" opacity="0.3"/>
                  <clipPath id="hurricane-clip">
                    <path d="M72 52 Q72 100 82 150 Q92 200 92 248 L108 248 Q108 200 118 150 Q128 100 128 52 Z"/>
                  </clipPath>
                  <g clipPath="url(#hurricane-clip)">
                    {cocktail.ingredients && cocktail.ingredients.map((ingredient, index) => {
                      const y = 248 - (proportions.slice(0, index + 1).reduce((a, b) => a + parseFloat(b), 0) * 1.96);
                      const prevY = index === 0 ? 248 : 248 - (proportions.slice(0, index).reduce((a, b) => a + parseFloat(b), 0) * 1.96);
                      const color = getIngredientColor(ingredient);
                      return (
                        <rect key={index} 
                              className="glass-layer"
                              x="70" 
                              y={y} 
                              width="60" 
                              height={prevY - y} 
                              fill={color} 
                              opacity="0.8"/>
                      );
                    })}
                  </g>
                </>
              )}
              
              {glassType === 'rocks' && (
                <>
                  <rect x="60" y="80" width="80" height="120" 
                        fill="none" 
                        stroke="#333" 
                        strokeWidth="2" 
                        rx="5"/>
                  <clipPath id="rocks-clip">
                    <rect x="62" y="82" width="76" height="116" rx="3"/>
                  </clipPath>
                  <g clipPath="url(#rocks-clip)">
                    {cocktail.ingredients && cocktail.ingredients.map((ingredient, index) => {
                      const y = 198 - (proportions.slice(0, index + 1).reduce((a, b) => a + parseFloat(b), 0) * 1.16);
                      const prevY = index === 0 ? 198 : 198 - (proportions.slice(0, index).reduce((a, b) => a + parseFloat(b), 0) * 1.16);
                      const color = getIngredientColor(ingredient);
                      return (
                        <rect key={index} 
                              className="glass-layer"
                              x="62" 
                              y={y} 
                              width="76" 
                              height={prevY - y} 
                              fill={color} 
                              opacity="0.8"/>
                      );
                    })}
                  </g>
                </>
              )}
              
              {glassType === 'wine' && (
                <>
                  {/* Wine glass bowl */}
                  <ellipse cx="100" cy="110" rx="50" ry="60" 
                          fill="none" 
                          stroke="#333" 
                          strokeWidth="3"/>
                  {/* Stem */}
                  <line x1="100" y1="170" x2="100" y2="250" 
                        stroke="#333" 
                        strokeWidth="3"/>
                  {/* Base */}
                  <ellipse cx="100" cy="250" rx="35" ry="5" fill="#333" opacity="0.3"/>
                  <line x1="70" y1="250" x2="130" y2="250" 
                        stroke="#333" 
                        strokeWidth="3"/>
                  
                  <clipPath id="wine-clip">
                    <ellipse cx="100" cy="110" rx="47" ry="57"/>
                  </clipPath>
                  <g clipPath="url(#wine-clip)">
                    {cocktail.ingredients && cocktail.ingredients.map((ingredient, index) => {
                      const totalHeight = 114; // Height of wine glass bowl
                      const y = 167 - (proportions.slice(0, index + 1).reduce((a, b) => a + parseFloat(b), 0) * totalHeight / 100);
                      const prevY = index === 0 ? 167 : 167 - (proportions.slice(0, index).reduce((a, b) => a + parseFloat(b), 0) * totalHeight / 100);
                      const color = getIngredientColor(ingredient);
                      
                      return (
                        <rect key={index} 
                              className="glass-layer"
                              x="50" 
                              y={y} 
                              width="100" 
                              height={prevY - y} 
                              fill={color} 
                              opacity="0.8"/>
                      );
                    })}
                  </g>
                </>
              )}
              
              {glassType === 'flute' && (
                <>
                  {/* Champagne flute */}
                  <path d="M85 50 Q85 100 90 150 L90 200 L110 200 L110 150 Q115 100 115 50 Z" 
                        fill="none" 
                        stroke="#333" 
                        strokeWidth="3"/>
                  {/* Stem */}
                  <line x1="100" y1="200" x2="100" y2="250" 
                        stroke="#333" 
                        strokeWidth="3"/>
                  {/* Base */}
                  <ellipse cx="100" cy="250" rx="30" ry="5" fill="#333" opacity="0.3"/>
                  <line x1="75" y1="250" x2="125" y2="250" 
                        stroke="#333" 
                        strokeWidth="3"/>
                  
                  <clipPath id="flute-clip">
                    <path d="M87 52 Q87 100 92 150 L92 198 L108 198 L108 150 Q113 100 113 52 Z"/>
                  </clipPath>
                  <g clipPath="url(#flute-clip)">
                    {cocktail.ingredients && cocktail.ingredients.map((ingredient, index) => {
                      const totalHeight = 146; // Height of flute
                      const y = 198 - (proportions.slice(0, index + 1).reduce((a, b) => a + parseFloat(b), 0) * totalHeight / 100);
                      const prevY = index === 0 ? 198 : 198 - (proportions.slice(0, index).reduce((a, b) => a + parseFloat(b), 0) * totalHeight / 100);
                      const color = getIngredientColor(ingredient);
                      
                      return (
                        <rect key={index} 
                              className="glass-layer"
                              x="85" 
                              y={y} 
                              width="30" 
                              height={prevY - y} 
                              fill={color} 
                              opacity="0.8"/>
                      );
                    })}
                  </g>
                </>
              )}
              
              {glassType === 'beer' && (
                <>
                  <path d="M70 50 L70 220 Q70 240 90 240 L110 240 Q130 240 130 220 L130 50 Z" 
                        fill="none" 
                        stroke="#333" 
                        strokeWidth="2"/>
                  <clipPath id="beer-clip">
                    <path d="M72 52 L72 220 Q72 238 90 238 L110 238 Q128 238 128 220 L128 52 Z"/>
                  </clipPath>
                  <g clipPath="url(#beer-clip)">
                    {cocktail.ingredients && cocktail.ingredients.map((ingredient, index) => {
                      const y = 238 - (proportions.slice(0, index + 1).reduce((a, b) => a + parseFloat(b), 0) * 1.86);
                      const prevY = index === 0 ? 238 : 238 - (proportions.slice(0, index).reduce((a, b) => a + parseFloat(b), 0) * 1.86);
                      const color = getIngredientColor(ingredient);
                      return (
                        <rect key={index} 
                              className="glass-layer"
                              x="70" 
                              y={y} 
                              width="60" 
                              height={prevY - y} 
                              fill={color} 
                              opacity="0.8"/>
                      );
                    })}
                  </g>
                </>
              )}
            </svg>
          </div>
          
          <div className="ingredients-detail">
            <h3>Ingredients</h3>
            <div className="ingredients-list">
              {cocktail.ingredients && cocktail.ingredients.map((ingredient, index) => (
                <div key={index} className="ingredient-item">
                  <div className="ingredient-line" style={{ backgroundColor: getIngredientColor(ingredient) }}></div>
                  <span className="ingredient-icon">{ingredientIcons[ingredient] || ingredientIcons.default}</span>
                  <span className="ingredient-name">{ingredient}</span>
                  <span className="ingredient-proportion">{proportions[index]}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to get color for each ingredient
const getIngredientColor = (ingredient) => {
  const colorMap = {
    "ジン": "#E3F2FD",
    "ウォッカ": "#F3E5F5",
    "ラム": "#EFEBE9",
    "テキーラ": "#FFF3E0",
    "ウイスキー": "#E0F2F1",
    "バーボン": "#EFEBE9",
    "スコッチウイスキー": "#D7CCC8",
    "ブランデー": "#FCE4EC",
    "コニャック": "#F3E5F5",
    "ビール": "#FFF8E1",
    "赤ワイン": "#880E4F",
    "白ワイン": "#FFF9C4",
    "カシスリキュール": "#4A148C",
    "ピーチリキュール": "#FFCCBC",
    "カンパリ": "#D32F2F",
    "アマレット": "#6D4C41",
    "コアントロー": "#FF6F00",
    "ホワイトキュラソー": "#FFF3E0",
    "ブルーキュラソー": "#0288D1",
    "ライチリキュール": "#F8BBD0",
    "マリブリキュール": "#FFF8E1",
    "チェリーブランデー": "#AD1457",
    "トニックウォーター": "#E0F7FA",
    "ソーダ": "#F5F5F5",
    "コーラ": "#3E2723",
    "ジンジャーエール": "#FFE082",
    "オレンジジュース": "#FF9800",
    "レモンジュース": "#FFF176",
    "ライムジュース": "#C6FF00",
    "グレープフルーツジュース": "#FFAB91",
    "パイナップルジュース": "#FFD54F",
    "トマトジュース": "#E53935",
    "クランベリージュース": "#C62828",
    "レモネード": "#FFF59D",
    "グレナデンシロップ": "#E91E63",
    "砂糖": "#FFFFFF",
    "塩": "#FAFAFA",
    "ライム": "#9CCC65",
    "レモン": "#FFEB3B"
  };
  
  return colorMap[ingredient] || "#BDBDBD";
};

export default CocktailDetailPage;